import React, { useEffect, useMemo, useState } from "react";
import { API } from "../App";
import axios from "axios";
import { Activity, Brain, ChevronRight, Droplets, Heart, Moon, Sparkles, Sun } from "lucide-react";

const PHASES = {
  menstrual: { title: "Menstrual", subtitle: "Rest, replenish and be gentle with yourself.", tone: "from-[#f5d8e4] to-[#fff1f5]", icon: Droplets },
  follicular: { title: "Follicular", subtitle: "Energy may be building — a good time for fresh starts.", tone: "from-[#ffe3cf] to-[#fff7ef]", icon: Sun },
  ovulation: { title: "Ovulation", subtitle: "You may feel more social and energetic around this phase.", tone: "from-[#ffe7b8] to-[#fff8e8]", icon: Sparkles },
  luteal: { title: "Luteal", subtitle: "Slow down, protect your energy and prioritize comfort.", tone: "from-[#dce4f6] to-[#f4f6fc]", icon: Moon },
  unknown: { title: "Your cycle", subtitle: "Log a period to unlock personalized phase insights.", tone: "from-[#eee8e2] to-[#fbfaf8]", icon: Heart }
};

const MOODS = ["Calm", "Happy", "Tired", "Low", "Irritable"];
const SYMPTOMS = ["Cramps", "Bloating", "Headache", "Tenderness", "Fatigue"];

const DashboardCompanion = ({ onAsk }) => {
  const [cycle, setCycle] = useState(null);
  const [mood, setMood] = useState("");
  const [symptoms, setSymptoms] = useState([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios.get(`${API}/periods/cycle-info`).then(res => setCycle(res.data)).catch(() => setCycle(null));
  }, []);

  const phase = PHASES[cycle?.current_phase] || PHASES.unknown;
  const PhaseIcon = phase.icon;
  const symptomText = symptoms.length ? ` I also have ${symptoms.join(", ")}.` : "";

  const toggleSymptom = (item) => setSymptoms(prev => prev.includes(item) ? prev.filter(x => x !== item) : [...prev, item]);

  const saveAndAsk = () => {
    if (!mood && !symptoms.length) return;
    setSaved(true);
    const prompt = `I feel ${mood || "unspecified"} today.${symptomText} My current Gentleflow cycle phase is ${phase.title}. Please give me gentle, practical wellness guidance and tell me when a symptom should be discussed with a healthcare professional.`;
    onAsk(prompt);
    setTimeout(() => setSaved(false), 1800);
  };

  return (
    <div className="fixed left-4 top-24 z-40 hidden w-[330px] max-w-[calc(100vw-32px)] xl:block" data-testid="dashboard-companion">
      <div className={`rounded-[26px] border border-[#E8E3DB] bg-gradient-to-br ${phase.tone} p-5 shadow-[0_18px_55px_rgba(74,25,66,.10)] backdrop-blur-sm`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/70"><PhaseIcon className="h-5 w-5 text-[#7f526b]" /></div>
            <div><p className="text-[10px] font-semibold uppercase tracking-[.16em] text-[#8A7A85]">Cycle insight</p><h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#4A4A4A]">{phase.title} phase</h3></div>
          </div>
          {cycle?.cycle_day && <span className="rounded-full bg-white/70 px-2.5 py-1 text-[11px] font-medium text-[#6d5b65]">Day {cycle.cycle_day}</span>}
        </div>
        <p className="mt-3 text-xs leading-relaxed text-[#5B4956]">{phase.subtitle}</p>

        <div className="mt-4 border-t border-white/70 pt-4">
          <div className="mb-2 flex items-center gap-2"><Activity className="h-4 w-4 text-[#C084A0]" /><span className="text-xs font-semibold text-[#4A4A4A]">Quick mood & symptom check</span></div>
          <div className="flex flex-wrap gap-1.5">{MOODS.map(item => <button key={item} onClick={() => setMood(item)} className={`rounded-full border px-2.5 py-1 text-[11px] transition-all ${mood === item ? "border-[#C084A0] bg-[#C084A0] text-white" : "border-white bg-white/70 text-[#5B4956] hover:border-[#C084A0]"}`}>{item}</button>)}</div>
          <div className="mt-2 flex flex-wrap gap-1.5">{SYMPTOMS.map(item => <button key={item} onClick={() => toggleSymptom(item)} className={`rounded-full border px-2.5 py-1 text-[11px] transition-all ${symptoms.includes(item) ? "border-[#8d5a78] bg-[#8d5a78] text-white" : "border-white bg-white/70 text-[#5B4956] hover:border-[#8d5a78]"}`}>{item}</button>)}</div>
          <button onClick={saveAndAsk} disabled={!mood && !symptoms.length} className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-[#4A1942] px-4 py-2.5 text-xs font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-40">{saved ? "Sent to Self-Consultant ✓" : "Get tailored guidance"}<ChevronRight className="h-3.5 w-3.5" /></button>
        </div>
      </div>
    </div>
  );
};

export default DashboardCompanion;
